import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import JobCard from "@/components/JobCard";
import { toast, Toaster } from "sonner";
import { FileText, Bookmark, ClipboardList, Sparkles, Bot, ShieldCheck } from "lucide-react";

const GRADE_LEVELS = ["Middle School", "High School", "College", "Not in School"];
const TRANSPORT = ["Walk", "Bike", "Public Transit", "Car"];
const WORK_AUTH = ["Yes", "No", "Unsure"];
const RESUME_STATUS = ["none", "in-progress", "complete"];

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [saved, setSaved] = useState([]);
  const [apps, setApps] = useState([]);
  const [recs, setRecs] = useState([]);
  const [fallback, setFallback] = useState([]);
  const [form, setForm] = useState({
    name: user?.name || "",
    date_of_birth: user?.date_of_birth || "",
    city: user?.city || "",
    state: user?.state || "",
    grade_level: user?.grade_level || "",
    education_level: user?.education_level || "",
    availability: user?.availability || "",
    work_authorization: user?.work_authorization || "",
    resume_status: user?.resume_status || "none",
    skills: (user?.skills||[]).join(", "),
    interests: (user?.interests||[]).join(", "),
    career_interests: (user?.career_interests||[]).join(", "),
    transportation: user?.transportation || [],
    bio: user?.bio || "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, a, r] = await Promise.all([
      api.get("/me/saved-jobs"),
      api.get("/me/applications"),
      api.get("/ai/recommendations").catch(()=>({data:{jobs:[], fallback:[]}})),
    ]);
    setSaved(s.data.jobs || []);
    setApps(a.data.applications || []);
    setRecs(r.data.jobs || []);
    setFallback(r.data.fallback || []);
  };

  useEffect(()=>{ load(); }, []);

  const toggleTransport = (t) => {
    const cur = new Set(form.transportation);
    if (cur.has(t)) cur.delete(t); else cur.add(t);
    setForm({ ...form, transportation: [...cur] });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        name: form.name,
        date_of_birth: form.date_of_birth || undefined,
        city: form.city, state: form.state,
        grade_level: form.grade_level,
        education_level: form.education_level,
        availability: form.availability,
        work_authorization: form.work_authorization || undefined,
        resume_status: form.resume_status,
        skills: form.skills.split(",").map(s=>s.trim()).filter(Boolean),
        interests: form.interests.split(",").map(s=>s.trim()).filter(Boolean),
        career_interests: form.career_interests.split(",").map(s=>s.trim()).filter(Boolean),
        transportation: form.transportation,
        bio: form.bio,
      });
      await refresh();
      toast.success("Profile saved");
      load();
    } catch(e){ toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="nh-container py-10">
      <Toaster position="top-center"/>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-4xl">Hey {user?.name?.split(" ")[0] || "friend"} 👋</h1>
          <p className="text-nh-muted mt-1">Your NextHire dashboard — apps, saves, and profile in one place.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/career-coach" className="nh-btn nh-btn-secondary text-xs" data-testid="dash-coach-link"><Bot size={14}/> Career Coach</Link>
          <Link to="/work-permit" className="nh-btn nh-btn-ghost text-xs" data-testid="dash-permit-link"><ShieldCheck size={14}/> Work Permit</Link>
          <Link to="/resume-builder" className="nh-btn nh-btn-primary text-xs" data-testid="dash-resume-link"><FileText size={14}/> Resume Builder</Link>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-8">
        {[
          { icon: <Bookmark/>, label: "Saved", count: saved.length, color: "bg-nh-mint" },
          { icon: <ClipboardList/>, label: "Applications", count: apps.length, color: "bg-nh-sky" },
          { icon: <Sparkles/>, label: "AI matches", count: recs.length, color: "bg-nh-accent" },
        ].map((s,i)=>(
          <div key={i} className="nh-card p-5 flex items-center gap-4">
            <div className={`h-12 w-12 rounded-lg border-2 border-black ${s.color} flex items-center justify-center`}>{s.icon}</div>
            <div>
              <div className="text-xs uppercase font-bold text-nh-muted">{s.label}</div>
              <div className="text-3xl font-display font-black">{s.count}</div>
            </div>
          </div>
        ))}
      </div>

      <section className="mt-10 nh-card p-6">
        <h2 className="font-display font-black text-2xl">Your Profile</h2>
        <p className="text-xs text-nh-muted mt-1">Your contact info is never shown publicly. We use it only to compute eligibility and match jobs.</p>

        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <div><label className="text-xs font-bold uppercase">Name</label><input className="nh-input mt-1" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="profile-name"/></div>
          <div><label className="text-xs font-bold uppercase">Date of Birth</label><input type="date" className="nh-input mt-1" value={form.date_of_birth?.slice(0,10) || ""} onChange={(e)=>setForm({...form, date_of_birth:e.target.value})} data-testid="profile-dob"/></div>
          <div><label className="text-xs font-bold uppercase">City</label><input className="nh-input mt-1" value={form.city} onChange={(e)=>setForm({...form, city:e.target.value})} data-testid="profile-city"/></div>
          <div><label className="text-xs font-bold uppercase">State (2-letter)</label><input maxLength={2} className="nh-input mt-1 uppercase" value={form.state} onChange={(e)=>setForm({...form, state:e.target.value.toUpperCase()})} data-testid="profile-state"/></div>
          <div>
            <label className="text-xs font-bold uppercase">Grade Level</label>
            <select className="nh-input mt-1" value={form.grade_level} onChange={(e)=>setForm({...form, grade_level:e.target.value})} data-testid="profile-grade">
              <option value="">Select…</option>
              {GRADE_LEVELS.map(g=><option key={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase">Work Authorization</label>
            <select className="nh-input mt-1" value={form.work_authorization} onChange={(e)=>setForm({...form, work_authorization:e.target.value})} data-testid="profile-workauth">
              <option value="">Select…</option>
              {WORK_AUTH.map(g=><option key={g}>{g}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-bold uppercase">Transportation Available</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {TRANSPORT.map(t=>(
                <button type="button" key={t} onClick={()=>toggleTransport(t)}
                  className={`nh-btn text-xs ${form.transportation.includes(t) ? "nh-btn-secondary" : "nh-btn-ghost"}`}
                  data-testid={`transport-${t.toLowerCase().replace(/\s/g,'-')}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div><label className="text-xs font-bold uppercase">Education Level</label><input className="nh-input mt-1" value={form.education_level} onChange={(e)=>setForm({...form, education_level:e.target.value})} placeholder="High School — Grade 10"/></div>
          <div><label className="text-xs font-bold uppercase">Availability</label><input className="nh-input mt-1" value={form.availability} onChange={(e)=>setForm({...form, availability:e.target.value})} placeholder="Weekends, After school"/></div>
          <div><label className="text-xs font-bold uppercase">Resume Status</label>
            <select className="nh-input mt-1" value={form.resume_status} onChange={(e)=>setForm({...form, resume_status:e.target.value})} data-testid="profile-resume-status">
              {RESUME_STATUS.map(g=><option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div><label className="text-xs font-bold uppercase">Career Interests</label><input className="nh-input mt-1" value={form.career_interests} onChange={(e)=>setForm({...form, career_interests:e.target.value})} placeholder="Nursing, coding, marketing"/></div>
          <div className="md:col-span-2"><label className="text-xs font-bold uppercase">Skills</label><input className="nh-input mt-1" value={form.skills} onChange={(e)=>setForm({...form, skills:e.target.value})} placeholder="Customer service, Excel"/></div>
          <div className="md:col-span-2"><label className="text-xs font-bold uppercase">Interests</label><input className="nh-input mt-1" value={form.interests} onChange={(e)=>setForm({...form, interests:e.target.value})} placeholder="tech, sports, gaming"/></div>
          <div className="md:col-span-2"><label className="text-xs font-bold uppercase">Bio</label><textarea rows={3} className="nh-input mt-1" value={form.bio} onChange={(e)=>setForm({...form, bio:e.target.value})}/></div>
        </div>
        <button onClick={save} className="nh-btn nh-btn-primary mt-4" disabled={saving} data-testid="profile-save">
          {saving ? "Saving…" : "Save Profile"}
        </button>
      </section>

      <section className="mt-10">
        <div className="flex items-end justify-between flex-wrap gap-2">
          <h2 className="font-display font-black text-2xl">Recommended for you</h2>
          <Link to="/career-coach" className="nh-btn nh-btn-ghost text-xs"><Bot size={14}/> Ask AI Coach</Link>
        </div>
        {recs.length === 0 ? <p className="text-sm text-nh-muted mt-2">Add DOB, skills & interests to unlock personalized picks.</p> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
            {recs.slice(0,6).map(j=><JobCard key={j.id} job={j} testIdPrefix="rec"/>)}
          </div>
        )}
        {fallback.length > 0 && (
          <div className="mt-6 nh-card bg-nh-accent p-5">
            <p className="font-display font-black text-lg">Not many jobs open for your age yet — try these alternatives:</p>
            <ul className="mt-3 space-y-2 text-sm">
              {fallback.map((f,i)=>(
                <li key={i}>
                  • <b>{f.title}</b> — {f.why}
                  {f.url && <> · <a className="underline" href={f.url} target="_blank" rel="noreferrer">visit</a></>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="mt-10">
        <h2 className="font-display font-black text-2xl">Your Applications</h2>
        {apps.length === 0 ? <p className="text-sm text-nh-muted mt-2">You haven't applied yet. <Link className="underline" to="/jobs">Browse jobs</Link>.</p> : (
          <div className="mt-4 nh-card p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-black text-white text-left"><tr><th className="p-3">Job</th><th className="p-3">Company</th><th className="p-3">Status</th><th className="p-3">Date</th></tr></thead>
              <tbody>
                {apps.map(a=>(
                  <tr key={a.id} className="border-t-2 border-black" data-testid={`app-row-${a.id}`}>
                    <td className="p-3 font-bold">{a.job?.title || "—"}</td>
                    <td className="p-3">{a.job?.company_name || "—"}</td>
                    <td className="p-3"><span className="nh-tag bg-nh-mint">{a.status}</span></td>
                    <td className="p-3 text-nh-muted">{new Date(a.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-10 mb-16">
        <h2 className="font-display font-black text-2xl">Saved Jobs</h2>
        {saved.length === 0 ? <p className="text-sm text-nh-muted mt-2">Bookmark jobs to review later.</p> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
            {saved.map(j=><JobCard key={j.id} job={j} testIdPrefix="saved"/>)}
          </div>
        )}
      </section>
    </div>
  );
}
